// Owner-only diagnostic tool for stress-testing Universal Capture.
// Route: /owner/ai-test-bench (gated by Kenneth's UUID in App.js).
//
// Test scans:
//   - Reuse the production enumerate-room-items Edge Function unchanged
//   - Use Kenneth's own room_id/home_id as placeholders for the FK insert
//     (the Edge Function rejects null FKs at 400), then immediately
//     UPDATE the row to is_test=true, room_id=null, home_id=null
//   - Persist items + processing metrics into test_metadata jsonb so past
//     scans can be re-rendered later (Edge Function doesn't store items)
//   - Never write anything to room_inventory
//
// Casavio Tone Principle still applies on this internal screen. Keep copy
// product-focused and item-identity-focused. No user judgments.

import { useEffect, useRef, useState } from 'react'
import { supabase } from '../supabase'
import { convertHeicIfNeeded } from '../utils/convertHeicIfNeeded'
import { stripExif } from '../utils/stripExif'
import { compressImage } from '../imageUtils'
import CasavioLogo from './shared/CasavioLogo'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', danger: '#B03020',
  dark: '#1C1209',
}
const f = 'Georgia, serif'
const fs = 'Calibri, sans-serif'

const ROOM_TYPES = ['Kitchen', 'Living', 'Bedroom', 'Bathroom', 'Dining', 'Office', 'Garage', 'Outdoor', 'Other']
const SETTINGS = ['My home', 'Friend\'s home', 'Open house', 'Other location']
const LIGHTING = ['Bright', 'Normal', 'Dim']
const ISSUE_TAGS = [
  { id: 'missed_items', label: 'Missed items' },
  { id: 'wrong_brand', label: 'Wrong brand' },
  { id: 'wrong_category', label: 'Wrong category' },
  { id: 'over_curated', label: 'Over-curated' },
  { id: 'under_curated', label: 'Under-curated' },
  { id: 'wrong_room', label: 'Wrong room interpretation' },
  { id: 'hallucinated', label: 'Hallucinated items' },
]

function tokenizeChip(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
}

function confDotColor(conf) {
  if (conf === 'high') return C.success
  if (conf === 'medium') return C.amber
  return C.muted
}

function ConfDot({ conf }) {
  if (!conf) return null
  return <span style={{ display: 'inline-block', width: '7px', height: '7px', borderRadius: '50%', background: confDotColor(conf), marginRight: '4px' }} />
}

function chipStyle(active, color = C.amber) {
  return {
    display: 'inline-block',
    padding: '5px 11px',
    borderRadius: '999px',
    border: `1px solid ${active ? color : C.border}`,
    background: active ? color : '#fff',
    color: active ? '#fff' : C.brown,
    fontFamily: fs, fontSize: '12px',
    cursor: 'pointer',
    fontWeight: active ? '600' : '400',
    transition: 'all 120ms ease',
  }
}

const inp = {
  width: '100%', background: '#fff', border: `1px solid ${C.border}`,
  borderRadius: '8px', padding: '8px 10px', fontSize: '13px',
  fontFamily: fs, color: C.brown, boxSizing: 'border-box', outline: 'none',
}

const sectionHeader = {
  fontSize: '11px', color: C.muted, letterSpacing: '2px',
  textTransform: 'uppercase', fontFamily: fs, fontWeight: '700',
  marginBottom: '10px', marginTop: '20px',
}

const SPIN_STYLE = `@keyframes testbench-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`

function Spinner({ size = 18, color = C.amber }) {
  return (
    <div
      style={{
        width: `${size}px`, height: `${size}px`,
        border: `2px solid ${color}33`,
        borderTopColor: color,
        borderRadius: '50%',
        animation: 'testbench-spin 0.9s linear infinite',
      }}
    />
  )
}

export default function AITestBench({ session }) {
  const userId = session?.user?.id

  // Placeholder room/home for the Edge Function's FK requirement.
  // We null these out immediately after the function returns.
  const [placeholder, setPlaceholder] = useState(null) // { roomId, homeId }
  const [placeholderError, setPlaceholderError] = useState(null)

  // Queue of files awaiting processing.
  const [queue, setQueue] = useState([]) // [{ id, file, previewUrl }]
  const [running, setRunning] = useState(false)
  const [activeQueueId, setActiveQueueId] = useState(null)

  // Live results from this session (newest first by insert order).
  const [results, setResults] = useState([]) // [Result]
  // Past test scans loaded from DB.
  const [pastScans, setPastScans] = useState([])
  const [pastLoading, setPastLoading] = useState(true)
  const [expandedPastId, setExpandedPastId] = useState(null)

  // Export modal.
  const [exportOpen, setExportOpen] = useState(false)
  const [exportText, setExportText] = useState('')
  const [exportCopied, setExportCopied] = useState(false)

  const cameraInputRef = useRef(null)
  const libraryInputRef = useRef(null)
  const queueIdCounterRef = useRef(0)

  // -- Load helpers ----------------------------------------------------------

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const { data: rooms, error } = await supabase
          .from('rooms')
          .select('id, home_id')
          .not('home_id', 'is', null)
          .limit(1)
        if (cancelled) return
        if (error || !rooms || rooms.length === 0) {
          setPlaceholderError('No room found on this account. Create a room first.')
          return
        }
        setPlaceholder({ roomId: rooms[0].id, homeId: rooms[0].home_id })
      } catch (err) {
        if (!cancelled) setPlaceholderError(err?.message || 'Could not load room context.')
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!userId) return
    loadPastScans()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  async function loadPastScans() {
    setPastLoading(true)
    const { data, error } = await supabase
      .from('room_scan_sessions')
      .select('id, created_at, source_photo_urls, items_detected, status, test_metadata')
      .eq('is_test', true)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(100)
    if (!error && data) setPastScans(data)
    setPastLoading(false)
  }

  // -- File queueing ---------------------------------------------------------

  const nextQueueId = () => {
    queueIdCounterRef.current += 1
    return `q-${queueIdCounterRef.current}`
  }

  async function addFiles(fileList) {
    const files = Array.from(fileList || [])
    if (files.length === 0) return
    const additions = []
    for (const f of files) {
      if (!f.type.startsWith('image/') && !/\.(heic|heif)$/i.test(f.name || '')) continue
      additions.push({
        id: nextQueueId(),
        file: f,
        name: f.name,
        previewUrl: URL.createObjectURL(f),
      })
    }
    setQueue(prev => [...prev, ...additions])
  }

  function removeFromQueue(id) {
    setQueue(prev => {
      const out = prev.filter(q => q.id !== id)
      const removed = prev.find(q => q.id === id)
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl)
      return out
    })
  }

  function clearQueue() {
    queue.forEach(q => { if (q.previewUrl) URL.revokeObjectURL(q.previewUrl) })
    setQueue([])
  }

  // -- Processing ------------------------------------------------------------

  async function runAll() {
    if (!placeholder || running) return
    setRunning(true)
    for (const item of queue) {
      setActiveQueueId(item.id)
      try {
        const result = await processOne(item)
        setResults(prev => [result, ...prev])
      } catch (err) {
        console.error('[AITestBench] processing error', err)
        setResults(prev => [{
          kind: 'error',
          queueId: item.id,
          previewUrl: item.previewUrl,
          errorMessage: err?.message || 'Unknown error',
        }, ...prev])
      }
    }
    setActiveQueueId(null)
    setRunning(false)
    setQueue([])
    // Reload past scans so they show the new rows.
    loadPastScans()
  }

  async function processOne(item) {
    const startedAt = performance.now()
    // 1. Preprocess: HEIC → JPEG, strip EXIF, compress.
    const jpeg = await convertHeicIfNeeded(item.file)
    const stripped = await stripExif(jpeg)
    const processed = await compressImage(stripped, 1600, 0.80)
    const base64 = await new Promise((resolve, reject) => {
      const r = new FileReader()
      r.onload = () => resolve(String(r.result).split(',')[1] || '')
      r.onerror = () => reject(new Error('FileReader failed'))
      r.readAsDataURL(processed)
    })

    // 2. Call the production Edge Function with placeholder room/home.
    const { data, error } = await supabase.functions.invoke('enumerate-room-items', {
      body: {
        photo_base64: base64,
        room_id: placeholder.roomId,
        home_id: placeholder.homeId,
        media_type: 'image/jpeg',
      },
    })
    if (error) throw error
    if (!data || !data.session_id) throw new Error('No session_id returned')

    const elapsedMs = Math.round(performance.now() - startedAt)
    const items = Array.isArray(data.items) ? data.items : []
    const keyCount = items.filter(it => it.ai_details?.should_log !== false).length

    // 3. Promote the row to a test row and persist items + metrics for re-render.
    const testMetadataInitial = {
      items,
      processed_ms: elapsedMs,
      processed_at: new Date().toISOString(),
    }
    await supabase
      .from('room_scan_sessions')
      .update({
        is_test: true,
        room_id: null,
        home_id: null,
        test_metadata: testMetadataInitial,
      })
      .eq('id', data.session_id)

    return {
      kind: 'live',
      sessionId: data.session_id,
      photoUrl: data.photo_url,
      items,
      itemsDetected: items.length,
      keyCount,
      elapsedMs,
      createdAt: new Date().toISOString(),
      // Rating draft state for this card:
      rating: 0,
      issues: [],
      roomType: '',
      setting: '',
      lighting: '',
      note: '',
      saving: false,
      saved: false,
    }
  }

  // -- Rating UI helpers (live results) --------------------------------------

  function updateResult(sessionId, patch) {
    setResults(prev => prev.map(r => r.sessionId === sessionId ? { ...r, ...patch } : r))
  }

  function toggleIssueLive(sessionId, issueId) {
    setResults(prev => prev.map(r => {
      if (r.sessionId !== sessionId) return r
      const has = r.issues.includes(issueId)
      return { ...r, issues: has ? r.issues.filter(x => x !== issueId) : [...r.issues, issueId] }
    }))
  }

  async function saveRating(result, sourcePast) {
    const sessionId = result.sessionId || result.id
    if (!sessionId) return

    const md = sourcePast?.test_metadata || result.test_metadata || {}
    const items = (md.items && Array.isArray(md.items)) ? md.items : (result.items || [])
    const processedMs = md.processed_ms ?? result.elapsedMs
    const processedAt = md.processed_at ?? result.createdAt

    const newMetadata = {
      items,
      processed_ms: processedMs,
      processed_at: processedAt,
      rating: result.rating || 0,
      issues: result.issues || [],
      room_type: tokenizeChip(result.roomType || ''),
      setting: tokenizeChip(result.setting || ''),
      lighting: tokenizeChip(result.lighting || ''),
      note: (result.note || '').trim(),
      rated_at: new Date().toISOString(),
    }

    if (result.sessionId) {
      updateResult(sessionId, { saving: true })
    } else {
      setPastScans(prev => prev.map(p => p.id === sessionId ? { ...p, _saving: true } : p))
    }

    const { error } = await supabase
      .from('room_scan_sessions')
      .update({ test_metadata: newMetadata })
      .eq('id', sessionId)

    if (result.sessionId) {
      updateResult(sessionId, { saving: false, saved: !error })
    } else {
      setPastScans(prev => prev.map(p => p.id === sessionId
        ? { ...p, _saving: false, _saved: !error, test_metadata: newMetadata }
        : p,
      ))
    }
  }

  // -- Past scan editing helpers ---------------------------------------------

  function setPastField(sessionId, field, value) {
    setPastScans(prev => prev.map(p => p.id === sessionId
      ? { ...p, _draft: { ...(p._draft || hydrateDraft(p)), [field]: value }, _saved: false }
      : p,
    ))
  }

  function togglePastIssue(sessionId, issueId) {
    setPastScans(prev => prev.map(p => {
      if (p.id !== sessionId) return p
      const draft = p._draft || hydrateDraft(p)
      const has = (draft.issues || []).includes(issueId)
      const issues = has ? draft.issues.filter(x => x !== issueId) : [...(draft.issues || []), issueId]
      return { ...p, _draft: { ...draft, issues }, _saved: false }
    }))
  }

  function hydrateDraft(past) {
    const md = past.test_metadata || {}
    return {
      rating: md.rating || 0,
      issues: Array.isArray(md.issues) ? md.issues : [],
      roomType: detokenize(md.room_type, ROOM_TYPES),
      setting: detokenize(md.setting, SETTINGS),
      lighting: detokenize(md.lighting, LIGHTING),
      note: md.note || '',
    }
  }

  function detokenize(slug, options) {
    if (!slug) return ''
    return options.find(o => tokenizeChip(o) === slug) || ''
  }

  // -- Export ----------------------------------------------------------------

  function openExport() {
    const rows = pastScans.slice(0, 50).map(p => ({
      id: p.id,
      created_at: p.created_at,
      items_detected: p.items_detected,
      source_photo_urls: p.source_photo_urls,
      test_metadata: p.test_metadata,
    }))
    setExportText(JSON.stringify(rows, null, 2))
    setExportCopied(false)
    setExportOpen(true)
  }

  async function copyExport() {
    try {
      await navigator.clipboard.writeText(exportText)
      setExportCopied(true)
      setTimeout(() => setExportCopied(false), 1500)
    } catch {
      /* clipboard might be blocked; user can select manually */
    }
  }

  function downloadExport() {
    const blob = new Blob([exportText], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `casavio-test-scans-${new Date().toISOString().slice(0, 10)}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // -- Renderers -------------------------------------------------------------

  const headerBar = (
    <div style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: C.dark, borderBottom: `2px solid ${C.amber}`,
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '0 20px', height: '50px',
    }}>
      <CasavioLogo size={15} color={C.amber} style={{ marginRight: '12px', flexShrink: 0 }} />
      <a href="/owner" style={{ color: '#CCC', textDecoration: 'none', fontSize: '12px', padding: '3px 10px', borderRadius: '6px', background: '#2A1E12', border: '1px solid #3D2C1A', fontFamily: fs }}>
        ← Founder Command Center
      </a>
      <div style={{ flex: 1 }} />
      <span style={{ color: C.muted, fontSize: '11px', fontFamily: fs }}>AI Test Bench · Kenneth only</span>
    </div>
  )

  function renderBoundingBoxOverlay(photoUrl, items, height = '32vh') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', background: '#000' }}>
        <div style={{ position: 'relative', display: 'inline-block', maxHeight: height }}>
          {photoUrl && (
            <img
              src={photoUrl}
              alt=""
              style={{ display: 'block', maxWidth: '100%', maxHeight: height, width: 'auto', height: 'auto' }}
            />
          )}
          {(items || []).filter(it => it.bounding_box).map((it, idx) => {
            const b = it.bounding_box
            const num = idx + 1
            return (
              <div
                key={idx}
                style={{
                  position: 'absolute',
                  left: `${b.x_min * 100}%`,
                  top: `${b.y_min * 100}%`,
                  width: `${(b.x_max - b.x_min) * 100}%`,
                  height: `${(b.y_max - b.y_min) * 100}%`,
                  border: `2px solid ${C.amber}`,
                  background: 'rgba(184,114,31,0.1)',
                  borderRadius: '3px',
                }}
              >
                <span style={{
                  position: 'absolute', top: '-10px', left: '-10px',
                  background: C.amber, color: '#fff',
                  width: '20px', height: '20px', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: f, fontSize: '11px', fontWeight: '700',
                  border: '2px solid #fff',
                }}>
                  {num}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  function renderItemsList(items) {
    if (!items || items.length === 0) return (
      <div style={{ fontFamily: fs, fontSize: '12px', color: C.muted, padding: '8px 0' }}>
        No items returned.
      </div>
    )
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {items.map((it, idx) => {
          const num = idx + 1
          const det = it.ai_details || {}
          const keyFlag = det.should_log === false ? '⊘' : '✓'
          const brand = det.brand?.value
          const brandConf = det.brand?.confidence
          const model = det.model?.value
          const modelConf = det.model?.confidence
          const cat = det.category_suggestion || '—'
          const refined = it.refined_label || it.label || '—'
          return (
            <div key={idx} style={{
              fontFamily: fs, fontSize: '12px', color: C.brown,
              padding: '6px 8px', background: '#FAF6EE',
              borderRadius: '6px', lineHeight: 1.45,
              display: 'flex', flexWrap: 'wrap', gap: '6px 12px', alignItems: 'baseline',
            }}>
              <span style={{
                background: C.amber, color: '#fff',
                width: '18px', height: '18px', borderRadius: '50%',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '10px', fontWeight: '700', flexShrink: 0,
              }}>{num}</span>
              <span style={{ fontWeight: '700', color: C.brown }}>{refined}</span>
              <span style={{ color: det.should_log === false ? C.muted : C.success, fontWeight: '700' }}>
                {keyFlag} {det.should_log === false ? 'skipped' : 'key'}
              </span>
              <span style={{ color: C.muted }}>cat: {cat}</span>
              {brand && <span><ConfDot conf={brandConf} />brand: {brand}</span>}
              {model && <span><ConfDot conf={modelConf} />model: {model}</span>}
              {det.color && <span style={{ color: C.muted }}>color: {det.color}</span>}
              {det.material && <span style={{ color: C.muted }}>material: {det.material}</span>}
            </div>
          )
        })}
      </div>
    )
  }

  function renderRatingPanel({
    rating, issues, roomType, setting, lighting, note,
    onRating, onIssueToggle, onRoomType, onSetting, onLighting, onNote,
    onSave, saving, saved,
  }) {
    return (
      <>
        <div style={sectionHeader}>Context</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div>
            <div style={{ fontSize: '11px', color: C.muted, fontFamily: fs, marginBottom: '6px' }}>Room type</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {ROOM_TYPES.map(r => (
                <span key={r} onClick={() => onRoomType(r === roomType ? '' : r)} style={chipStyle(r === roomType)}>{r}</span>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: C.muted, fontFamily: fs, marginBottom: '6px' }}>Setting</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {SETTINGS.map(s => (
                <span key={s} onClick={() => onSetting(s === setting ? '' : s)} style={chipStyle(s === setting)}>{s}</span>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: C.muted, fontFamily: fs, marginBottom: '6px' }}>Lighting</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {LIGHTING.map(l => (
                <span key={l} onClick={() => onLighting(l === lighting ? '' : l)} style={chipStyle(l === lighting)}>{l}</span>
              ))}
            </div>
          </div>
        </div>

        <div style={sectionHeader}>Overall accuracy</div>
        <div style={{ display: 'flex', gap: '4px' }}>
          {[1, 2, 3, 4, 5].map(n => (
            <button
              key={n}
              onClick={() => onRating(n)}
              aria-label={`${n} star${n === 1 ? '' : 's'}`}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontSize: '24px', padding: '2px 4px', lineHeight: 1,
                color: n <= (rating || 0) ? C.amber : C.border,
              }}
            >
              ★
            </button>
          ))}
        </div>

        <div style={sectionHeader}>Issues</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {ISSUE_TAGS.map(t => (
            <span
              key={t.id}
              onClick={() => onIssueToggle(t.id)}
              style={chipStyle(issues.includes(t.id), C.danger)}
            >
              {t.label}
            </span>
          ))}
        </div>

        <div style={sectionHeader}>Note (optional)</div>
        <input
          type="text"
          value={note || ''}
          onChange={e => onNote(e.target.value)}
          placeholder="—"
          style={inp}
        />

        <div style={{ marginTop: '16px' }}>
          <button
            onClick={onSave}
            disabled={saving}
            style={{
              background: saved ? C.success : `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
              color: '#fff', border: 'none', borderRadius: '8px',
              padding: '10px 18px', fontFamily: f, fontSize: '13px', fontWeight: '700',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving…' : saved ? 'Rating saved ✓' : 'Save rating'}
          </button>
        </div>
      </>
    )
  }

  function renderLiveResult(r) {
    if (r.kind === 'error') {
      return (
        <div key={r.queueId} style={{
          background: C.card, border: `1px solid ${C.border}`, borderRadius: '12px',
          padding: '14px', marginBottom: '14px',
        }}>
          <div style={{ fontFamily: f, fontSize: '14px', color: C.danger, fontWeight: '700', marginBottom: '8px' }}>
            Processing failed
          </div>
          <div style={{ fontFamily: fs, fontSize: '12px', color: C.muted }}>
            {r.errorMessage}
          </div>
        </div>
      )
    }

    return (
      <div key={r.sessionId} style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: '12px',
        marginBottom: '14px', overflow: 'hidden',
      }}>
        {renderBoundingBoxOverlay(r.photoUrl, r.items)}
        <div style={{ padding: '14px 16px 18px' }}>
          <div style={{
            fontFamily: fs, fontSize: '12px', color: C.muted,
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            marginBottom: '12px', gap: '12px', flexWrap: 'wrap',
          }}>
            <div>
              Processed in {(r.elapsedMs / 1000).toFixed(1)}s · {r.itemsDetected} item{r.itemsDetected === 1 ? '' : 's'} detected · {r.keyCount} key
            </div>
            <a
              href={r.photoUrl}
              target="_blank"
              rel="noreferrer"
              style={{ color: C.amber, fontSize: '11px', textDecoration: 'none' }}
            >
              open photo →
            </a>
          </div>

          {renderItemsList(r.items)}

          {renderRatingPanel({
            rating: r.rating, issues: r.issues,
            roomType: r.roomType, setting: r.setting, lighting: r.lighting,
            note: r.note,
            onRating: n => updateResult(r.sessionId, { rating: n, saved: false }),
            onIssueToggle: id => toggleIssueLive(r.sessionId, id),
            onRoomType: v => updateResult(r.sessionId, { roomType: v, saved: false }),
            onSetting: v => updateResult(r.sessionId, { setting: v, saved: false }),
            onLighting: v => updateResult(r.sessionId, { lighting: v, saved: false }),
            onNote: v => updateResult(r.sessionId, { note: v, saved: false }),
            onSave: () => saveRating(r),
            saving: r.saving,
            saved: r.saved,
          })}
        </div>
      </div>
    )
  }

  function renderPastRow(past) {
    const md = past.test_metadata || {}
    const isExpanded = expandedPastId === past.id
    const draft = past._draft || hydrateDraft(past)
    const thumb = (past.source_photo_urls || [])[0]
    const items = Array.isArray(md.items) ? md.items : []
    const keyCount = items.filter(it => it.ai_details?.should_log !== false).length
    const dt = past.created_at ? new Date(past.created_at) : null

    return (
      <div key={past.id} style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: '12px',
        marginBottom: '10px', overflow: 'hidden',
      }}>
        <div
          onClick={() => setExpandedPastId(prev => prev === past.id ? null : past.id)}
          style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            padding: '10px 14px', cursor: 'pointer',
          }}
        >
          {thumb ? (
            <img src={thumb} alt="" style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0, background: C.bg }} />
          ) : (
            <div style={{ width: '48px', height: '48px', borderRadius: '6px', background: C.bg, flexShrink: 0 }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: f, fontSize: '13px', color: C.brown }}>
              {dt ? dt.toLocaleString() : '—'}
            </div>
            <div style={{ fontFamily: fs, fontSize: '11px', color: C.muted, marginTop: '2px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {md.room_type && <span style={{ background: C.bg, padding: '1px 6px', borderRadius: '999px', border: `1px solid ${C.border}` }}>{md.room_type}</span>}
              {md.setting && <span style={{ background: C.bg, padding: '1px 6px', borderRadius: '999px', border: `1px solid ${C.border}` }}>{md.setting}</span>}
              <span>{items.length} item{items.length === 1 ? '' : 's'}, {keyCount} key</span>
            </div>
          </div>
          <div style={{ fontFamily: fs, fontSize: '12px', color: md.rating ? C.amber : C.muted, fontWeight: '700' }}>
            {md.rating ? `${'★'.repeat(md.rating)}${'☆'.repeat(5 - md.rating)}` : 'Unrated'}
          </div>
        </div>

        {isExpanded && (
          <div style={{ borderTop: `1px solid ${C.border}` }}>
            {thumb && renderBoundingBoxOverlay(thumb, items, '28vh')}
            <div style={{ padding: '14px 16px 18px' }}>
              <div style={{ fontFamily: fs, fontSize: '12px', color: C.muted, marginBottom: '12px' }}>
                {md.processed_ms != null ? `Processed in ${(md.processed_ms / 1000).toFixed(1)}s · ` : ''}
                {items.length} item{items.length === 1 ? '' : 's'} detected · {keyCount} key
              </div>
              {renderItemsList(items)}
              {renderRatingPanel({
                rating: draft.rating,
                issues: draft.issues,
                roomType: draft.roomType,
                setting: draft.setting,
                lighting: draft.lighting,
                note: draft.note,
                onRating: n => setPastField(past.id, 'rating', n),
                onIssueToggle: id => togglePastIssue(past.id, id),
                onRoomType: v => setPastField(past.id, 'roomType', v),
                onSetting: v => setPastField(past.id, 'setting', v),
                onLighting: v => setPastField(past.id, 'lighting', v),
                onNote: v => setPastField(past.id, 'note', v),
                onSave: () => saveRating({
                  id: past.id,
                  rating: draft.rating,
                  issues: draft.issues,
                  roomType: draft.roomType,
                  setting: draft.setting,
                  lighting: draft.lighting,
                  note: draft.note,
                  test_metadata: md,
                }, past),
                saving: !!past._saving,
                saved: !!past._saved,
              })}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: fs }}>
      <style>{SPIN_STYLE}</style>
      {headerBar}

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px 18px 60px' }}>
        {/* Title + subtitle + badge */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontFamily: f, fontSize: '26px', color: C.brown, margin: '0 0 4px' }}>AI Test Bench</h1>
            <p style={{ color: C.muted, fontSize: '13px', margin: 0, fontFamily: fs, lineHeight: 1.5 }}>
              Stress-test Universal Capture in the wild. Photos here never touch production inventory.
            </p>
          </div>
          <span style={{
            background: C.dark, color: C.amber,
            fontSize: '10px', letterSpacing: '1.5px', textTransform: 'uppercase',
            padding: '4px 9px', borderRadius: '999px', fontFamily: fs, fontWeight: '700',
            flexShrink: 0, marginTop: '4px',
          }}>
            Owner only
          </span>
        </div>

        {placeholderError && (
          <div style={{
            marginTop: '16px',
            background: '#F6E5DF', color: C.danger,
            border: `1px solid ${C.danger}55`, borderRadius: '8px',
            padding: '10px 14px', fontSize: '13px',
          }}>
            {placeholderError}
          </div>
        )}

        {/* Upload area */}
        <div style={{
          marginTop: '22px',
          background: C.card, border: `1px dashed ${C.border}`, borderRadius: '14px',
          padding: '20px',
        }}>
          <div style={{ fontFamily: f, fontSize: '15px', color: C.brown, fontWeight: '700', marginBottom: '4px' }}>
            Drop in a scan
          </div>
          <div style={{ fontFamily: fs, fontSize: '12px', color: C.muted, marginBottom: '14px' }}>
            Pick one or many photos. We'll process them serially through the same Edge Function production uses.
          </div>

          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={e => { addFiles(e.target.files); e.target.value = '' }}
          />
          <input
            ref={libraryInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={e => { addFiles(e.target.files); e.target.value = '' }}
          />

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={() => cameraInputRef.current?.click()}
              disabled={running || !placeholder}
              style={{
                background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
                color: '#fff', border: 'none', borderRadius: '8px',
                padding: '10px 16px', fontFamily: f, fontSize: '13px', fontWeight: '700',
                cursor: running || !placeholder ? 'not-allowed' : 'pointer',
                opacity: running || !placeholder ? 0.6 : 1,
                display: 'inline-flex', alignItems: 'center', gap: '6px',
              }}
            >
              📷 Take a Photo
            </button>
            <button
              onClick={() => libraryInputRef.current?.click()}
              disabled={running || !placeholder}
              style={{
                background: '#fff', color: C.brown,
                border: `1px solid ${C.border}`, borderRadius: '8px',
                padding: '10px 16px', fontFamily: fs, fontSize: '13px', fontWeight: '600',
                cursor: running || !placeholder ? 'not-allowed' : 'pointer',
                opacity: running || !placeholder ? 0.6 : 1,
                display: 'inline-flex', alignItems: 'center', gap: '6px',
              }}
            >
              🖼️ Add from Camera Roll
            </button>
          </div>

          {/* Queue */}
          {queue.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontFamily: fs, fontSize: '12px', color: C.muted }}>
                  {queue.length} photo{queue.length === 1 ? '' : 's'} ready
                </div>
                <button
                  onClick={clearQueue}
                  disabled={running}
                  style={{
                    background: 'transparent', color: C.muted,
                    border: 'none', cursor: running ? 'not-allowed' : 'pointer',
                    fontSize: '11px', fontFamily: fs, textDecoration: 'underline',
                  }}
                >
                  Clear all
                </button>
              </div>
              <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '6px' }}>
                {queue.map(q => {
                  const isActive = activeQueueId === q.id
                  return (
                    <div key={q.id} style={{ position: 'relative', flexShrink: 0 }}>
                      <img
                        src={q.previewUrl}
                        alt=""
                        style={{
                          width: '72px', height: '72px',
                          objectFit: 'cover', borderRadius: '8px',
                          background: C.bg, display: 'block',
                          opacity: isActive ? 0.55 : 1,
                        }}
                      />
                      {isActive && (
                        <div style={{
                          position: 'absolute', inset: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexDirection: 'column', gap: '4px',
                        }}>
                          <Spinner size={20} />
                          <span style={{ fontSize: '9px', color: '#fff', fontFamily: fs, textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>Processing…</span>
                        </div>
                      )}
                      {!isActive && !running && (
                        <button
                          onClick={() => removeFromQueue(q.id)}
                          aria-label="Remove from queue"
                          style={{
                            position: 'absolute', top: '-6px', right: '-6px',
                            width: '20px', height: '20px', borderRadius: '50%',
                            background: '#fff', border: `1px solid ${C.border}`,
                            cursor: 'pointer', padding: 0, lineHeight: 1,
                            fontSize: '12px', color: C.muted,
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>

              <button
                onClick={runAll}
                disabled={running || !placeholder}
                style={{
                  marginTop: '12px',
                  background: running || !placeholder ? C.border : `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
                  color: '#fff', border: 'none', borderRadius: '8px',
                  padding: '11px 18px', fontFamily: f, fontSize: '14px', fontWeight: '700',
                  cursor: running || !placeholder ? 'not-allowed' : 'pointer',
                  opacity: running || !placeholder ? 0.7 : 1,
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                }}
              >
                {running ? <><Spinner size={14} color="#fff" /> Running…</> : `Run AI on ${queue.length} photo${queue.length === 1 ? '' : 's'}`}
              </button>
            </div>
          )}
        </div>

        {/* Live results */}
        {results.length > 0 && (
          <div style={{ marginTop: '24px' }}>
            <div style={sectionHeader}>This session</div>
            {results.map(renderLiveResult)}
          </div>
        )}

        {/* Past scans log */}
        <div style={{ marginTop: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={{ fontFamily: f, fontSize: '17px', color: C.brown, fontWeight: '700' }}>
              Past test scans
            </div>
            <button
              onClick={openExport}
              disabled={pastScans.length === 0}
              style={{
                background: 'transparent', color: C.brown,
                border: `1px solid ${C.border}`, borderRadius: '8px',
                padding: '6px 12px', fontFamily: fs, fontSize: '12px',
                cursor: pastScans.length === 0 ? 'not-allowed' : 'pointer',
                opacity: pastScans.length === 0 ? 0.5 : 1,
              }}
            >
              Export
            </button>
          </div>

          {pastLoading ? (
            <div style={{ fontFamily: fs, fontSize: '13px', color: C.muted, padding: '20px 0' }}>
              Loading…
            </div>
          ) : pastScans.length === 0 ? (
            <div style={{
              fontFamily: fs, fontSize: '13px', color: C.muted,
              padding: '20px', background: C.card, border: `1px solid ${C.border}`,
              borderRadius: '10px', textAlign: 'center',
            }}>
              No past test scans yet. Process a photo above to populate the log.
            </div>
          ) : (
            pastScans.map(renderPastRow)
          )}
        </div>
      </div>

      {/* Export modal */}
      {exportOpen && (
        <div
          onClick={() => setExportOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.55)',
            zIndex: 1900, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: C.card, borderRadius: '14px',
              width: '100%', maxWidth: '720px', maxHeight: '90vh',
              display: 'flex', flexDirection: 'column',
              padding: '20px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div style={{ fontFamily: f, fontSize: '16px', color: C.brown, fontWeight: '700' }}>
                Export · last {Math.min(pastScans.length, 50)} test scans
              </div>
              <button
                onClick={() => setExportOpen(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.muted, fontSize: '18px' }}
              >
                ×
              </button>
            </div>
            <textarea
              readOnly
              value={exportText}
              style={{
                flex: 1, fontFamily: 'Menlo, Consolas, monospace', fontSize: '11px',
                background: '#FAF6EE', color: C.brown,
                border: `1px solid ${C.border}`, borderRadius: '8px',
                padding: '10px', minHeight: '320px', resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={copyExport}
                style={{
                  background: exportCopied ? C.success : '#fff',
                  color: exportCopied ? '#fff' : C.brown,
                  border: `1px solid ${exportCopied ? C.success : C.border}`,
                  borderRadius: '8px', padding: '8px 14px',
                  fontFamily: fs, fontSize: '13px', cursor: 'pointer',
                }}
              >
                {exportCopied ? 'Copied ✓' : 'Copy to clipboard'}
              </button>
              <button
                onClick={downloadExport}
                style={{
                  background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
                  color: '#fff', border: 'none', borderRadius: '8px',
                  padding: '8px 14px', fontFamily: f, fontSize: '13px', fontWeight: '700',
                  cursor: 'pointer',
                }}
              >
                Download as JSON
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
